import { CatalogicalGeneratedModel, ItemModel } from "lib/services/catalogical-models";

/** List visibility options */
export enum Visibility {
  /** Only visible to owner */
  PRIVATE = "private",
  /** Visible to everyone */
  PUBLIC = "public",
  /** Visible to specific domains or groups */
  SHARED = "shared",
}

/** Evaluation rule fact types */
export enum Facts {
  /** Job family name attribute */
  JOB_FAMILY = "jobFamilyName",
  SUB_TYPE = "tags",
}

/** Item within a list with metadata */
export interface ListItem extends ItemModel {
  /** Timestamp when item was added to list */
  addedAt: Date;
  /** Alias of user who added the item */
  addedBy: string;
  /** Current status of the item */
  status: string;
}

/** Complete list model with all properties */
export interface ListModel {
  /** Unique identifier for the list */
  listId: string;
  /** Display name of the list */
  name: string;
  /** Optional description of the list */
  description?: string;
  /** Alias of the list owner */
  ownerAlias: string;
  /** Items in the list with metadata */
  items: ListItem[];
  /** Total number of items in the list */
  itemCount: number;
  /** Visibility setting for the list */
  visibility: Visibility;
  /** Full item details for all items in the list */
  fullItems: ItemModel[];
  /** Domains allowed to access the list */
  allowedDomains: string[];
  /** Tags associated with the list */
  tags: string[];
  /** Rules for automatic list evaluation */
  evaluationRules?: Record<string, unknown>;
  /** Bindle for ownership authorization */
  ownershipBindle?: string;
  /** Bindle for shared access authorization */
  sharedWithBindle?: string;
  /** Autogenerated attributes by Catalogical */
  catalogicalGenerated?: CatalogicalGeneratedModel;
}

/** Response for getting multiple lists */
export interface GetListsResponse {
  /** Array of list models */
  lists: ListModel[];
  /** Total number of lists available */
  totalCount: number;
}

/** Response for getting a single list */
export interface GetListResponse {
  /** The requested list model */
  list: ListModel;
}

/** Request to create a new list */
export interface CreateListRequest {
  /** Name for the new list */
  name: string;
  /** Optional description */
  description?: string;
  /** Visibility setting */
  visibility?: string;
  /** Domains allowed to access */
  allowedDomains?: string[];
  /** Tags to associate with the list */
  tags?: string[];
  /** Initial item IDs to add */
  itemIds?: string[];
}

/** Request to update an existing list */
export interface UpdateListRequest {
  /** Updated name */
  name?: string;
  /** Updated description */
  description?: string;
  /** Updated visibility setting */
  visibility?: string;
  /** Updated allowed domains */
  allowedDomains?: string[];
  /** Updated tags */
  tags?: string[];
}

/** Request to add items to a list */
export interface AddItemToListRequest {
  /** Item IDs to add */
  itemIds: string[];
  /** Target list ID */
  listId?: string;
  /** Whether to auto-create list if it doesn't exist */
  autoCreate?: boolean;
  /** Name for auto-created list */
  name?: string;
  /** Domain for the list */
  domain?: string;
}

/** Response after adding items to a list */
export interface AddItemToListResponse {
  /** IDs of items successfully added */
  itemsAdded: string[];
  /** IDs of items that were skipped */
  itemsSkipped: string[];
  /** Whether a new list was created */
  listCreated: boolean;
  /** ID of the target list */
  listId: string;
  /** Whether the operation succeeded */
  success: boolean;
  /** Summary statistics */
  summary: {
    /** Number of items added */
    added: number;
    /** Number of items attempted */
    attempted: number;
    /** Number of items that failed */
    failed: number;
    /** Number of items skipped */
    skipped: number;
  };
}

/** Request to evaluate which lists apply to given attributes */
export interface EvaluateListsRequest {
  /** User/context attributes to evaluate against */
  attributes: Record<string, any>;
  /** Domains to filter lists by */
  domains?: string[];
  /** Whether to include full item details */
  includeItemDetails?: boolean;
  /** Maximum number of lists to return */
  limit?: number;
}

/** A list that applies to the evaluated attributes */
export interface ApplicableList {
  /** List description */
  description: string;
  /** IDs of items in the list */
  itemIds: string[];
  /** Unique list identifier */
  listId: string;
  /** List name */
  name: string;
  /** Reason why this list applies */
  reason: string;
  /** Score indicating how specific the match is */
  specificityScore: number;
  /** Full item details if requested */
  fullItems?: ItemModel[];
}

/** Response from list evaluation */
export interface EvaluateListsResponse {
  /** Lists that apply to the given attributes */
  applicableLists: ApplicableList[];
  /** Metadata about the evaluation */
  evaluationMetadata: {
    /** Whether result was served from cache */
    cacheHit: boolean;
    /** Time taken to evaluate in milliseconds */
    evaluationTimeMs: number;
    /** Whether item details were included */
    itemDetailsIncluded: boolean;
    /** Total number of rules evaluated */
    totalRulesEvaluated: number;
  };
  /** Pagination information */
  pagination: {
    /** Maximum results per page */
    limit: number;
    /** Current offset */
    offset: number;
    /** Total number of applicable lists */
    total: number;
  };
}
